import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Badge,
  Skeleton,
  Checkbox,
  TablePagination,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Key as KeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  GroupAdd as GroupAddIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config.js";

// API Service
const { API_BASE_URL } = config;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const subUserAPI = {
  getSubUsers: () =>
    axios.get(`${API_BASE_URL}/api/v1/getSubUsers`, {
      headers: getAuthHeaders(),
    }),

  getSubUserStats: () =>
    axios.get(`${API_BASE_URL}/api/v1/get-subuser-stats`, {
      headers: getAuthHeaders(),
    }),

  updateSubUserStatus: (id, status) =>
    axios.patch(
      `${API_BASE_URL}/api/v1/updateSubUserStatusById/${id}/status`,
      { isActive: status },
      {
        headers: getAuthHeaders(),
      }
    ),

  deleteSubUser: (id) =>
    axios.delete(`${API_BASE_URL}/api/v1/deleteSubUser/${id}`, {
      headers: getAuthHeaders(),
    }),

  bulkUpdateSubUsers: (updates) =>
    axios.post(`${API_BASE_URL}/api/v1/update-sub-users-bulk-update`, updates, {
      headers: getAuthHeaders(),
    }),
};

const SubUserManagementComponent = () => {
  const navigate = useNavigate();

  const [subUsers, setSubUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showLoginCode, setShowLoginCode] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
  };

  const loadSubUsers = async () => {
    try {
      setLoading(true);
      const response = await subUserAPI.getSubUsers();
      setSubUsers(response.data.data.subUsers || []);
    } catch (error) {
      showSnackbar("Error loading sub-users", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await subUserAPI.getSubUserStats();
      

      const apiData = response.data.data;
      if (apiData) {
        setStats({
          total: apiData.totalSubUsers || 0,
          active: apiData.activeSubUsers || 0,
          inactive: apiData.inactiveSubUsers || 0,
        });
      } else {
        const activeCount = subUsers.filter((u) => u.isActive).length;
        setStats({
          total: subUsers.length,
          active: activeCount,
          inactive: subUsers.length - activeCount,
        });
      }
    } catch (error) {
      console.error("Error loading stats:", error);

      const activeCount = subUsers.filter((u) => u.isActive).length;
      setStats({
        total: subUsers.length,
        active: activeCount,
        inactive: subUsers.length - activeCount,
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await loadSubUsers();
        await loadStats();
      } catch (error) {
        console.error("Error loading data:", error);
        showSnackbar("Error loading data", "error");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleToggleStatus = async (userId) => {
    try {
      const user = subUsers.find((u) => u._id === userId);
      const newStatus = !user.isActive;

      await subUserAPI.updateSubUserStatus(userId, newStatus);

      setSubUsers((prev) =>
        prev.map((user) =>
          user._id === userId ? { ...user, isActive: newStatus } : user
        )
      );

      showSnackbar(
        `User ${newStatus ? "activated" : "deactivated"} successfully`,
        "success"
      );
      loadStats();
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar("Error updating status", "error");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await subUserAPI.deleteSubUser(userId);
      setSubUsers((prev) => prev.filter((user) => user._id !== userId));
      showSnackbar("Sub-user deleted successfully", "success");
      setMenuAnchor(null);
      loadStats();
    } catch (error) {
      console.error("Error deleting user:", error);
      showSnackbar("Error deleting user", "error");
    }
  };

  const handleBulkAction = async (action) => {
    try {
      const updates = selectedUsers.map((userId) => ({
        _id: userId,
        action: action,
      }));

      await subUserAPI.bulkUpdateSubUsers(updates);
      showSnackbar(`Bulk ${action} completed successfully`, "success");
      setSelectedUsers([]);
      loadSubUsers();
      loadStats();
    } catch (error) {
      console.error("Error performing bulk action:", error);
      showSnackbar("Error performing bulk action", "error");
    }
  };

  const handleMenuOpen = (event, user) => {
   
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const toggleLoginCodeVisibility = (userId) => {
    setShowLoginCode((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUsers(filteredUsers.map((user) => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const getRoleColor = (role) => {
    switch (role.toLowerCase()) {
      case "admin":
        return "error";
      case "manager":
        return "warning";
      case "user":
        return "primary";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEditUserPermissions = (userId) => {
    navigate(`/cs/sub-user/permissions/${userId}`);
  };

  const handleResetPassword = (userId) => {
    navigate(`/cs/sub-user/reset-password/${userId}`);
  };

  const handleUpdateUser = (userId) => {
    navigate(`/cs/sub-user/update/${userId}`);
  };

  const filteredUsers = subUsers.filter((user) => {
    const matchesSearch =
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone.includes(searchQuery);

    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && user.isActive) ||
      (statusFilter === "inactive" && !user.isActive);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const LoadingSkeleton = () => (
    <TableRow>
      {[...Array(8)].map((_, index) => (
        <TableCell key={index}>
          <Skeleton animation="wave" height={40} />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <Box
      sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div variants={cardVariants}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                Sub-User Management
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Manage and monitor all sub-users in your organization
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ px: 3 }}
              >
                <Link
                  to="/cs/SubUserCreation"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  Add Sub-User
                </Link>
              </Button>
            </Box>
          </Box>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div variants={cardVariants}>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" color="text.secondary">
                        Total Sub-Users
                      </Typography>
                      <Typography
                        variant="h3"
                        color="primary.main"
                        fontWeight="bold"
                      >
                        {stats.total}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ mt: 1 }}
                      >
                        <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        Active management
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{ bgcolor: "primary.main", width: 56, height: 56 }}
                    >
                      <PersonIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" color="text.secondary">
                        Active Users
                      </Typography>
                      <Typography
                        variant="h3"
                        color="success.main"
                        fontWeight="bold"
                      >
                        {stats.active}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {stats.total > 0
                          ? Math.round((stats.active / stats.total) * 100)
                          : 0}
                        % of total
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{ bgcolor: "success.main", width: 56, height: 56 }}
                    >
                      <CheckCircleIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" color="text.secondary">
                        Inactive Users
                      </Typography>
                      <Typography
                        variant="h3"
                        color="error.main"
                        fontWeight="bold"
                      >
                        {stats.inactive}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Require attention
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{ bgcolor: "error.main", width: 56, height: 56 }}
                    >
                      <CancelIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>

        {/* Filters and Search */}
        <motion.div variants={cardVariants}>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    label="Role"
                  >
                    <MenuItem value="all">All Roles</MenuItem>
                    <MenuItem value="CA">CA</MenuItem>
                    <MenuItem value="CS">CS</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="employee">Employee</MenuItem>
                    <MenuItem value="lawyer">Lawyer</MenuItem>
                    <MenuItem value="company">Company</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                >
                  {selectedUsers.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<GroupAddIcon />}
                      onClick={() => handleBulkAction("activate")}
                    >
                      Bulk Actions ({selectedUsers.length})
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => {
                      setSearchQuery("");
                      setRoleFilter("all");
                      setStatusFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Sub-Users Table */}
        <motion.div variants={cardVariants}>
          <Paper sx={{ borderRadius: 2, overflow: "hidden", boxShadow: 2 }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "grey.50" }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          selectedUsers.length === filteredUsers.length &&
                          filteredUsers.length > 0
                        }
                        indeterminate={
                          selectedUsers.length > 0 &&
                          selectedUsers.length < filteredUsers.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Prefix</TableCell>
                    <TableCell>Login Code</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, index) => (
                      <LoadingSkeleton key={index} />
                    ))
                  ) : (
                    <AnimatePresence>
                      {paginatedUsers.map((user, index) => (
                        <motion.tr
                          key={user._id}
                          component={TableRow}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ delay: index * 0.05 }}
                          hover
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedUsers.includes(user._id)}
                              onChange={() => handleSelectUser(user._id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Badge
                                variant="dot"
                                color={user.isActive ? "success" : "error"}
                              >
                                <Avatar sx={{ bgcolor: "primary.light" }}>
                                  {user.username.charAt(0).toUpperCase()}
                                </Avatar>
                              </Badge>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {user.username}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {user.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {user.email}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <PhoneIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {user.phone}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.role}
                              color={getRoleColor(user.role)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {user.subUserPrefix}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                              >
                                {showLoginCode[user._id]
                                  ? user.loginCode
                                  : "••••••••"}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  toggleLoginCodeVisibility(user._id)
                                }
                              >
                                {showLoginCode[user._id] ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={user.isActive}
                                  onChange={() => handleToggleStatus(user._id)}
                                  color="primary"
                                />
                              }
                              label={user.isActive ? "Active" : "Inactive"}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(user.lastLogin)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditUserPermissions(user._id)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="More actions">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, user)}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </motion.div>
      </motion.div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
           
            handleEditUserPermissions(selectedUser?._id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Permissions</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            
            handleUpdateUser(selectedUser?._id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Update User</ListItemText>
        </MenuItem>
       
        <MenuItem
          onClick={() => {
            
            handleToggleStatus(selectedUser?._id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {selectedUser?.isActive ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedUser?.isActive ? "Deactivate" : "Activate"} User
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
           
            handleDeleteUser(selectedUser?._id);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubUserManagementComponent;
